import { Link } from "react-router-dom";
import { ArrowRight, MapPin, Phone, ShieldCheck, IndianRupee, Headphones, Clock } from "lucide-react";
import PageHero from "@/components/PageHero";
import { Eyebrow, FadeUp } from "@/components/Reveal";
import { useQuote } from "@/components/QuoteProvider";
import { CONTACT, IMAGES, PILLARS } from "@/data/site";

const PILLAR_ICONS = { ShieldCheck, IndianRupee, Headphones, Clock };

const GLANCE = [
    { k: "Location", v: "H-60, Durga Industrial Area, Sahibabad, Ghaziabad, U.P." },
    { k: "Board Classes", v: "Single layer · Double layer · Multilayer · Metal core" },
    { k: "Market Segments Served", v: "12 segments, from power electronics to consumer products" },
    { k: "Enquiry Data Accepted", v: "Gerber (RS-274X) + drill files, up to 15 MB per upload" },
    { k: "Order Range", v: "Prototypes through repeat production quantities" },
    { k: "Support", v: "Two direct phone lines and a technical enquiry form" },
];

export default function About() {
    const { openQuote } = useQuote();
    return (
        <>
            <PageHero
                crumb="About Us"
                eyebrow="Unimicron Technology India"
                titleLines={["About", "Unimicron Technology", "India."]}
                lead="PCB manufacturing solutions for electronics manufacturers and industrial customers — built on quality, competitive pricing, reliable service and timely delivery."
            />

            {/* Overview */}
            <section id="overview" className="scroll-mt-24 bg-white py-20 md:py-28" data-testid="about-overview">
                <div className="mx-auto grid max-w-7xl gap-14 px-4 sm:px-6 lg:grid-cols-2 lg:gap-20 lg:px-8">
                    <div>
                        <FadeUp>
                            <Eyebrow>Company Overview</Eyebrow>
                            <h2 className="mt-5 text-3xl font-bold tracking-tight text-navy md:text-4xl">
                                PCB manufacturing, done with discipline
                            </h2>
                            <p className="mt-6 text-[15px] leading-relaxed text-slate-600">
                                Unimicron Technology India is a PCB manufacturing company based at
                                H-60, Durga Industrial Area, Sahibabad, Ghaziabad, Uttar Pradesh. We
                                provide PCB manufacturing solutions for electronics manufacturers and
                                industrial customers across a wide range of applications.
                            </p>
                            <p className="mt-4 text-[15px] leading-relaxed text-slate-600">
                                Our focus is straightforward — manufacture every board to the
                                customer's design and specification, maintain consistent quality at
                                every production stage, offer competitive pricing for both prototypes
                                and production volumes, and deliver on committed timelines. From
                                single layer and double layer PCBs to multilayer and metal core
                                boards, every order is handled with the same care and attention.
                            </p>
                            <p className="mt-4 text-[15px] leading-relaxed text-slate-600">
                                We work closely with our customers at the enquiry stage itself —
                                reviewing PCB requirements, Gerber data and quantities — so that what
                                reaches their production line is exactly what was promised.
                            </p>
                        </FadeUp>
                        <FadeUp className="mt-10 flex flex-wrap gap-4">
                            <button
                                onClick={openQuote}
                                data-testid="about-quote-btn"
                                className="border border-brand-red bg-brand-red px-7 py-3.5 text-[13px] font-semibold uppercase tracking-wider text-white transition-colors hover:bg-brand-redhover"
                            >
                                Get a Quote
                            </button>
                            <Link
                                to="/pcb-manufacturing"
                                data-testid="about-pcb-link"
                                className="group inline-flex items-center gap-2 border border-navy px-7 py-3.5 text-[13px] font-semibold uppercase tracking-wider text-navy transition-colors hover:bg-navy hover:text-white"
                            >
                                Our PCB Capabilities
                                <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
                            </Link>
                        </FadeUp>
                    </div>
                    <FadeUp className="relative">
                        <img
                            src={IMAGES.factoryDispatch}
                            alt="Packing and dispatch area at Unimicron Technology India"
                            className="h-[380px] w-full object-cover md:h-[520px]"
                        />
                        <div className="absolute -bottom-4 -right-4 hidden h-24 w-24 border-b-2 border-r-2 border-brand-red md:block" aria-hidden="true" />
                        <div className="absolute left-0 top-0 bg-navy-dark px-5 py-3 font-mono text-[10px] uppercase tracking-[0.2em] text-white">
                            Our Facility — Sahibabad, U.P.
                        </div>
                    </FadeUp>
                </div>
            </section>

            {/* Why choose us */}
            <section id="why-us" className="scroll-mt-24 border-y border-slate-200 bg-slate-50 py-20 md:py-28" data-testid="about-why-us">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <FadeUp>
                        <Eyebrow>Why Choose Us</Eyebrow>
                        <h2 className="mt-5 max-w-2xl text-3xl font-bold tracking-tight text-navy md:text-4xl">
                            What customers can expect from every order
                        </h2>
                    </FadeUp>
                    <div className="mt-12 grid gap-px border border-slate-200 bg-slate-200 sm:grid-cols-2 lg:grid-cols-4" data-testid="about-pillars">
                        {PILLARS.map((p, i) => {
                            const Icon = PILLAR_ICONS[p.icon];
                            return (
                                <FadeUp key={p.title} delay={i * 0.08} className="group bg-white p-7 transition-colors duration-200 hover:bg-navy">
                                    <Icon className="h-6 w-6 text-brand-red" strokeWidth={1.5} />
                                    <h3 className="mt-5 text-lg font-semibold text-navy transition-colors group-hover:text-white">{p.title}</h3>
                                    <p className="mt-3 text-[13.5px] leading-relaxed text-slate-500 transition-colors group-hover:text-slate-300">{p.desc}</p>
                                </FadeUp>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* At a glance */}
            <section id="at-a-glance" className="scroll-mt-24 bg-white py-20 md:py-28" data-testid="about-glance">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <FadeUp>
                        <Eyebrow>At a Glance</Eyebrow>
                        <h2 className="mt-5 max-w-2xl text-3xl font-bold tracking-tight text-navy md:text-4xl">
                            The essentials, in one place
                        </h2>
                    </FadeUp>
                    <div className="mt-12 grid gap-px border border-slate-200 bg-slate-200 sm:grid-cols-2 lg:grid-cols-3" data-testid="about-glance-grid">
                        {GLANCE.map((g, i) => (
                            <FadeUp key={g.k} delay={(i % 3) * 0.06} className="bg-white p-6">
                                <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-slate-400">{g.k}</p>
                                <p className="mt-2.5 text-[14px] font-medium leading-relaxed text-navy">{g.v}</p>
                            </FadeUp>
                        ))}
                    </div>
                </div>
            </section>

            {/* Who we serve */}
            <section id="who-we-serve" className="scroll-mt-24 border-t border-slate-200 bg-slate-50 py-20 md:py-28" data-testid="about-who-we-serve">
                <div className="mx-auto grid max-w-7xl gap-14 px-4 sm:px-6 lg:grid-cols-2 lg:gap-20 lg:px-8">
                    <FadeUp>
                        <Eyebrow>Who We Serve</Eyebrow>
                        <h2 className="mt-5 text-3xl font-bold tracking-tight text-navy md:text-4xl">
                            Serving electronics & industrial customers
                        </h2>
                        <p className="mt-6 text-[15px] leading-relaxed text-slate-600">
                            Our PCBs support manufacturers across power electronics, UPS and
                            inverters, energy meters, industrial automation, LED lighting,
                            automotive, networking, consumer products and more.
                        </p>
                        <div className="mt-8 flex items-start gap-3 border border-slate-200 bg-white p-5">
                            <MapPin className="mt-0.5 h-5 w-5 shrink-0 text-brand-red" />
                            <div>
                                <p className="font-mono text-[10.5px] uppercase tracking-[0.18em] text-slate-500">Works Address</p>
                                <p className="mt-1.5 text-[14px] leading-relaxed text-navy">{CONTACT.address}</p>
                            </div>
                        </div>
                        <div className="mt-4 flex flex-wrap gap-4">
                            <a href={CONTACT.phone1Href} className="inline-flex items-center gap-2 font-mono text-[13px] text-navy transition-colors hover:text-brand-red" data-testid="about-phone-1">
                                <Phone className="h-4 w-4 text-brand-red" /> {CONTACT.phone1}
                            </a>
                            <a href={CONTACT.phone2Href} className="inline-flex items-center gap-2 font-mono text-[13px] text-navy transition-colors hover:text-brand-red" data-testid="about-phone-2">
                                <Phone className="h-4 w-4 text-brand-red" /> {CONTACT.phone2}
                            </a>
                        </div>
                    </FadeUp>
                    <FadeUp className="relative">
                        <img
                            src={IMAGES.prodMask}
                            alt="Solder mask and legend printing on PCB panels"
                            className="h-[380px] w-full object-cover md:h-[480px]"
                        />
                        <div className="absolute bottom-0 left-0 bg-navy-dark px-5 py-3 font-mono text-[10px] uppercase tracking-[0.2em] text-white">
                            Solder Mask & Legend Printing
                        </div>
                    </FadeUp>
                </div>
            </section>
        </>
    );
}
